import { useState } from 'react';
import { deleteOrgFile } from '../../storage/FileSystemAdapter';
import { scanReferencesToEntity } from '../../utils/intelligence';
import { AUTHORITY_TYPES, AUTHORITY_STATUSES } from '../../utils/enums';
import Badge from '../shared/Badge';
import ConfirmDialog from '../shared/ConfirmDialog';
import { dateStatus } from '../../utils/dateStatus';
import {
  BG_CONTENT, BG_SURFACE, BG_SHELL, BG_MUTED,
  BTN_SECONDARY, BTN_DESTRUCTIVE_OUTLINE,
  TEXT_MUTED, TEXT_DISABLED, TEXT_HEADING, TEXT_DEFAULT,
  BORDER_DEFAULT, BRAND_PRIMARY_DARK,
  STATUS_GOOD, STATUS_BAD, STATUS_WARN, SURFACE_WHITE,
  ERROR_TEXT, ERROR_BG, ERROR_BORDER,
  FONT_BODY, FONT_LABEL, FONT_SMALL, FONT_TINY, FONT_LIST_ITEM,
} from '../../theme';

// ─── §11 RULE 42 — BLOCK DIALOG SUPPORT ──────────────────────────────────────
const HOLDER_NAV = {
  'risk': 'risks', 'asset': 'assets', 'supplier': 'suppliers',
  'incident': 'incidents', 'audit': 'audits', 'nonconformity': 'nonconformities',
  'objective': 'objectives', 'metric': 'metrics', 'document': 'documents',
  'legal-register': 'legal-register', 'management-review': 'management-reviews',
  'soa-control': 'soa', 'scope': 'scope',
};
function holderLabel(t, holderType, count) {
  const key = `deleteBlockedHolder_${holderType}_${count === 1 ? 'one' : 'other'}`;
  return t[key] || holderType;
}
function summariseHits(hits, t) {
  const counts = {};
  hits.forEach((h) => { counts[h.holderType] = (counts[h.holderType] || 0) + 1; });
  return Object.entries(counts).map(([type, n]) => `${n} ${holderLabel(t, type, n)}`).join(', ');
}

// ── I18N ──────────────────────────────────────────────────────────────────────
const T = {
  es: {
    loading:            'Cargando autoridades…',
    emptyTitle:         'No hay autoridades registradas.',
    emptyHint:          'Crea la primera autoridad con el botón "+ Nueva autoridad".',
    colName:            'Nombre',
    colType:            'Tipo',
    colCountry:         'País',
    colPurposes:        '# Propósitos',
    colLastContact:     'Último contacto',
    colStatus:          'Estado',
    colActions:         'Acciones',
    filterAll:          'Todos',
    filterType:         'Tipo',
    filterCountry:      'País',
    filterStatus:       'Estado',
    includeArchived:    'Incluir archivadas',
    editBtn:            'Editar',
    deleteBtn:          'Eliminar',
    deleteConfirm:      '¿Eliminar la autoridad "{name}"? Esta acción no se puede deshacer.',
    deleteError:        'Error al eliminar.',
    formCancel:         'Cancelar',
    badgeOverdue:       'Vencido',
    badgeSoon:          'Vence pronto',
    notSet:             '—',
    typeLabels: {
      'Data protection authority':          'Autoridad de protección de datos',
      'National CSIRT':                     'CSIRT nacional',
      'Sector regulator':                   'Regulador sectorial',
      'Law enforcement':                    'Fuerzas y cuerpos de seguridad',
      'NIS2 competent authority':           'Autoridad competente NIS2',
      'Critical infrastructure authority':  'Autoridad de infraestructuras críticas',
      'Standardisation body':               'Organismo de normalización',
      'Government cyber agency':            'Agencia gubernamental de ciberseguridad',
      'Other':                              'Otro',
    },
    statusLabels: {
      'Active':   'Activa',
      'Inactive': 'Inactiva',
    },
    deleteBlockedTitle:        'No se puede eliminar: existen referencias',
    deleteBlockedReferencedBy: 'Referenciado por',
    deleteBlockedHolder_risk_one:               'riesgo',
    deleteBlockedHolder_risk_other:             'riesgos',
    deleteBlockedHolder_asset_one:              'activo',
    deleteBlockedHolder_asset_other:            'activos',
    deleteBlockedHolder_supplier_one:           'proveedor',
    deleteBlockedHolder_supplier_other:         'proveedores',
    deleteBlockedHolder_incident_one:           'incidente',
    deleteBlockedHolder_incident_other:         'incidentes',
    deleteBlockedHolder_audit_one:              'auditoría',
    deleteBlockedHolder_audit_other:            'auditorías',
    deleteBlockedHolder_nonconformity_one:      'no conformidad',
    deleteBlockedHolder_nonconformity_other:    'no conformidades',
    deleteBlockedHolder_objective_one:          'objetivo',
    deleteBlockedHolder_objective_other:        'objetivos',
    deleteBlockedHolder_metric_one:             'métrica',
    deleteBlockedHolder_metric_other:           'métricas',
    deleteBlockedHolder_document_one:           'documento',
    deleteBlockedHolder_document_other:         'documentos',
    'deleteBlockedHolder_legal-register_one':   'entrada del registro legal',
    'deleteBlockedHolder_legal-register_other': 'entradas del registro legal',
    'deleteBlockedHolder_management-review_one':   'revisión por la dirección',
    'deleteBlockedHolder_management-review_other': 'revisiones por la dirección',
    'deleteBlockedHolder_soa-control_one':      'control del SoA',
    'deleteBlockedHolder_soa-control_other':    'controles del SoA',
    deleteBlockedHolder_scope_one:              'alcance',
    deleteBlockedHolder_scope_other:            'alcance',
  },
  en: {
    loading:            'Loading authorities…',
    emptyTitle:         'No authorities recorded yet.',
    emptyHint:          'Create the first authority using the "+ New authority" button.',
    colName:            'Name',
    colType:            'Type',
    colCountry:         'Country',
    colPurposes:        '# Purposes',
    colLastContact:     'Last contact',
    colStatus:          'Status',
    colActions:         'Actions',
    filterAll:          'All',
    filterType:         'Type',
    filterCountry:      'Country',
    filterStatus:       'Status',
    includeArchived:    'Include archived',
    editBtn:            'Edit',
    deleteBtn:          'Delete',
    deleteConfirm:      'Delete authority "{name}"? This cannot be undone.',
    deleteError:        'Delete failed.',
    formCancel:         'Cancel',
    badgeOverdue:       'Overdue',
    badgeSoon:          'Due soon',
    notSet:             '—',
    typeLabels: {
      'Data protection authority':          'Data protection authority',
      'National CSIRT':                     'National CSIRT',
      'Sector regulator':                   'Sector regulator',
      'Law enforcement':                    'Law enforcement',
      'NIS2 competent authority':           'NIS2 competent authority',
      'Critical infrastructure authority':  'Critical infrastructure authority',
      'Standardisation body':               'Standardisation body',
      'Government cyber agency':            'Government cyber agency',
      'Other':                              'Other',
    },
    statusLabels: {
      'Active':   'Active',
      'Inactive': 'Inactive',
    },
    deleteBlockedTitle:        'Cannot delete: references found',
    deleteBlockedReferencedBy: 'Referenced by',
    deleteBlockedHolder_risk_one:               'risk',
    deleteBlockedHolder_risk_other:             'risks',
    deleteBlockedHolder_asset_one:              'asset',
    deleteBlockedHolder_asset_other:            'assets',
    deleteBlockedHolder_supplier_one:           'supplier',
    deleteBlockedHolder_supplier_other:         'suppliers',
    deleteBlockedHolder_incident_one:           'incident',
    deleteBlockedHolder_incident_other:         'incidents',
    deleteBlockedHolder_audit_one:              'audit',
    deleteBlockedHolder_audit_other:            'audits',
    deleteBlockedHolder_nonconformity_one:      'nonconformity',
    deleteBlockedHolder_nonconformity_other:    'nonconformities',
    deleteBlockedHolder_objective_one:          'objective',
    deleteBlockedHolder_objective_other:        'objectives',
    deleteBlockedHolder_metric_one:             'metric',
    deleteBlockedHolder_metric_other:           'metrics',
    deleteBlockedHolder_document_one:           'document',
    deleteBlockedHolder_document_other:         'documents',
    'deleteBlockedHolder_legal-register_one':   'legal register entry',
    'deleteBlockedHolder_legal-register_other': 'legal register entries',
    'deleteBlockedHolder_management-review_one':   'management review',
    'deleteBlockedHolder_management-review_other': 'management reviews',
    'deleteBlockedHolder_soa-control_one':      'SoA control',
    'deleteBlockedHolder_soa-control_other':    'SoA controls',
    deleteBlockedHolder_scope_one:              'scope',
    deleteBlockedHolder_scope_other:            'scope',
  },
};

// ── COLOR MAPS ────────────────────────────────────────────────────────────────
const STATUS_COLORS = {
  'Active':   { bg: STATUS_GOOD.bg, text: BRAND_PRIMARY_DARK },
  'Inactive': { bg: BG_MUTED,       text: TEXT_MUTED },
};

// ── COMPONENT ─────────────────────────────────────────────────────────────────
export default function AuthorityList({ authorities, loading, org, lang, onEdit, onNavigate, onAuthoritiesChanged }) {
  const t = T[lang] || T.en;

  const [filterType,    setFilterType]    = useState('');
  const [filterCountry, setFilterCountry] = useState('');
  const [filterStatus,  setFilterStatus]  = useState('');
  const [includeArchived, setIncludeArchived] = useState(false);
  const [confirmState,  setConfirmState]  = useState(null);
  const [scanningDelete,setScanningDelete]= useState(false);
  const [listError,     setListError]     = useState('');

  const safeAuthorities = Array.isArray(authorities) ? authorities : [];

  // §11 rule 32 — soft-delete: Inactive records are archived. Hidden by default.
  const visibleAuthorities = safeAuthorities.filter((a) => includeArchived ? true : a.status !== 'Inactive');

  const countriesInData = Array.from(new Set(visibleAuthorities.map((a) => a.country).filter(Boolean))).sort();

  const filtered = visibleAuthorities.filter((a) => {
    if (filterType    && a.type    !== filterType)    return false;
    if (filterCountry && a.country !== filterCountry) return false;
    if (filterStatus  && a.status  !== filterStatus)  return false;
    return true;
  });

  const showError = (msg) => { setListError(msg); setTimeout(() => setListError(''), 4000); };

  const renderBlockContent = (hits) => (
    <div>
      <h3 style={{ ...FONT_BODY, fontWeight: 700, color: TEXT_HEADING, margin: '0 0 0.5rem' }}>
        {t.deleteBlockedTitle}
      </h3>
      <p style={{ ...FONT_BODY, color: TEXT_DEFAULT, margin: '0 0 0.75rem' }}>
        {t.deleteBlockedReferencedBy} {summariseHits(hits, t)}
      </p>
      <div style={{ maxHeight: '40vh', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '0.35rem', marginBottom: '1.25rem' }}>
        {hits.map((h, i) => (
          <button
            key={i}
            type="button"
            onClick={() => { setConfirmState(null); onNavigate?.(HOLDER_NAV[h.holderType], null); }}
            style={{ ...BTN_SECONDARY, ...FONT_LABEL, borderRadius: 6, padding: '0.35rem 0.6rem', textAlign: 'left', cursor: 'pointer' }}
          >
            {`${holderLabel(t, h.holderType, 1)} ${h.holderRef}`}
          </button>
        ))}
      </div>
    </div>
  );

  const handleDeleteOne = async (authority) => {
    if (scanningDelete) return;
    setScanningDelete(true);
    let hits = [];
    try { hits = await scanReferencesToEntity(org.id, 'authority', authority.id); }
    catch { hits = []; }
    setScanningDelete(false);
    if (hits.length === 0) {
      const msg = t.deleteConfirm.replace('{name}', authority.shortName || authority.name || authority.ref || authority.id);
      setConfirmState({
        message: msg,
        confirmLabel: t.deleteBtn,
        onConfirm: async () => {
          setConfirmState(null);
          try {
            await deleteOrgFile(org.id, `authorities/${authority.id}.json`);
            onAuthoritiesChanged?.();
          } catch { showError(t.deleteError); }
        },
      });
      return;
    }
    setConfirmState({
      content: renderBlockContent(hits),
      confirmLabel: t.formCancel,
      confirmColor: BTN_SECONDARY,
      onConfirm: () => setConfirmState(null),
    });
  };

  // ── Styles ────────────────────────────────────────────────────────────────
  const thSt = {
    ...FONT_TINY, color: TEXT_DISABLED,
    letterSpacing: '0.04em', textTransform: 'uppercase',
    padding: '0.4rem 0.75rem', borderBottom: `1px solid ${BORDER_DEFAULT}`,
    fontWeight: 600, textAlign: 'left', background: BG_CONTENT, whiteSpace: 'nowrap',
  };
  const tdSt = {
    ...FONT_LIST_ITEM,
    padding: '0.55rem 0.75rem', borderBottom: `1px solid ${BG_SHELL}`,
    verticalAlign: 'middle',
  };
  const filterSelectSt = {
    ...FONT_SMALL,
    border: `1px solid ${BORDER_DEFAULT}`, borderRadius: 5,
    padding: '0.3rem 0.5rem', background: SURFACE_WHITE, color: TEXT_DEFAULT, cursor: 'pointer',
  };
  const btnSmall = {
    ...BTN_SECONDARY, ...FONT_TINY,
    borderRadius: 5, padding: '0.2rem 0.5rem', cursor: 'pointer',
  };

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div>
      {listError && (
        <div style={{ ...FONT_LABEL, marginBottom: '0.75rem', padding: '0.6rem 1rem', background: ERROR_BG, border: `1px solid ${ERROR_BORDER}`, borderRadius: 8, color: ERROR_TEXT }}>
          {listError}
        </div>
      )}

      {/* Filter bar */}
      {safeAuthorities.length > 0 && (
        <div style={{ display: 'flex', alignItems: 'flex-end', gap: '0.75rem', flexWrap: 'wrap', marginBottom: '1rem' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
            <span style={{ ...FONT_SMALL, color: TEXT_MUTED, textTransform: 'uppercase', letterSpacing: '0.04em' }}>{t.filterType}</span>
            <select value={filterType} onChange={(e) => setFilterType(e.target.value)} style={filterSelectSt}>
              <option value="">{t.filterAll}</option>
              {AUTHORITY_TYPES.map((v) => <option key={v} value={v}>{t.typeLabels[v]}</option>)}
            </select>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
            <span style={{ ...FONT_SMALL, color: TEXT_MUTED, textTransform: 'uppercase', letterSpacing: '0.04em' }}>{t.filterCountry}</span>
            <select value={filterCountry} onChange={(e) => setFilterCountry(e.target.value)} style={filterSelectSt}>
              <option value="">{t.filterAll}</option>
              {countriesInData.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
            <span style={{ ...FONT_SMALL, color: TEXT_MUTED, textTransform: 'uppercase', letterSpacing: '0.04em' }}>{t.filterStatus}</span>
            <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} style={filterSelectSt}>
              <option value="">{t.filterAll}</option>
              {AUTHORITY_STATUSES.map((v) => <option key={v} value={v}>{t.statusLabels[v]}</option>)}
            </select>
          </div>
          <label style={{ ...FONT_LABEL, color: TEXT_MUTED, display: 'flex', alignItems: 'center', gap: '0.4rem', cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={includeArchived}
              onChange={(e) => setIncludeArchived(e.target.checked)}
            />
            {t.includeArchived}
          </label>
        </div>
      )}

      {/* Loading */}
      {loading && (
        <p style={{ ...FONT_LABEL, color: TEXT_MUTED }}>{t.loading}</p>
      )}

      {/* Empty state */}
      {!loading && filtered.length === 0 && (
        <div style={{ background: BG_SURFACE, border: `1px solid ${BORDER_DEFAULT}`, borderRadius: 10, padding: '2.5rem', textAlign: 'center' }}>
          <p style={{ ...FONT_BODY, color: TEXT_MUTED, margin: '0 0 0.5rem' }}>{t.emptyTitle}</p>
          <p style={{ ...FONT_LABEL, color: TEXT_DISABLED, margin: 0 }}>{t.emptyHint}</p>
        </div>
      )}

      {/* Table */}
      {!loading && filtered.length > 0 && (
        <div style={{ overflowX: 'auto', borderRadius: 10, border: `1px solid ${BORDER_DEFAULT}` }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', background: BG_SURFACE }}>
            <thead>
              <tr>
                <th style={{ ...thSt, minWidth: 200 }}>{t.colName}</th>
                <th style={thSt}>{t.colType}</th>
                <th style={thSt}>{t.colCountry}</th>
                <th style={{ ...thSt, textAlign: 'center' }}>{t.colPurposes}</th>
                <th style={{ ...thSt, minWidth: 130 }}>{t.colLastContact}</th>
                <th style={thSt}>{t.colStatus}</th>
                <th style={thSt}>{t.colActions}</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((a, idx) => {
                const primary = a.shortName || a.name || a.ref || a.id;
                const secondary = a.shortName && a.name && a.shortName !== a.name ? a.name : '';
                const purposeCount = Array.isArray(a.purpose) ? a.purpose.length : 0;
                const ds = a.lastContactDate ? dateStatus(a.lastContactDate) : null;
                return (
                  <tr key={a.id} style={{ background: idx % 2 === 0 ? BG_SURFACE : 'transparent' }}>
                    <td style={tdSt}>
                      <div style={{ fontWeight: 600, color: TEXT_HEADING }}>{primary}</div>
                      {secondary && (
                        <div style={{ ...FONT_TINY, color: TEXT_MUTED }}>{secondary}</div>
                      )}
                    </td>
                    <td style={{ ...tdSt, ...FONT_LABEL, color: TEXT_MUTED }}>
                      {t.typeLabels[a.type] || a.type || t.notSet}
                    </td>
                    <td style={{ ...tdSt, ...FONT_LABEL, color: TEXT_MUTED }}>{a.country || t.notSet}</td>
                    <td style={{ ...tdSt, textAlign: 'center', ...FONT_LABEL, color: TEXT_DEFAULT }}>
                      {purposeCount}
                    </td>
                    <td style={{ ...tdSt, ...FONT_LABEL, color: TEXT_DEFAULT }}>
                      {(() => {
                        if (!a.lastContactDate) return <span style={{ color: TEXT_MUTED }}>{t.notSet}</span>;
                        return (
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', flexWrap: 'wrap' }}>
                            <span style={{ whiteSpace: 'nowrap' }}>{a.lastContactDate.slice(0, 10)}</span>
                            {ds && (
                              <span style={{
                                ...FONT_TINY,
                                fontWeight: 600,
                                background: ds === 'overdue' ? STATUS_BAD.bg : STATUS_WARN.bg,
                                color:      ds === 'overdue' ? STATUS_BAD.text : STATUS_WARN.text,
                                borderRadius: 4,
                                padding: '0.15rem 0.45rem',
                                whiteSpace: 'nowrap',
                              }}>
                                {ds === 'overdue' ? t.badgeOverdue : t.badgeSoon}
                              </span>
                            )}
                          </div>
                        );
                      })()}
                    </td>
                    <td style={tdSt}>
                      <Badge label={a.status} displayLabel={t.statusLabels[a.status] || a.status} colorMap={STATUS_COLORS} />
                    </td>
                    <td style={{ ...tdSt, whiteSpace: 'nowrap' }}>
                      <div style={{ display: 'flex', gap: '0.35rem' }}>
                        <button onClick={() => onEdit(a)} style={btnSmall}>{t.editBtn}</button>
                        <button
                          onClick={() => handleDeleteOne(a)}
                          disabled={scanningDelete}
                          style={{ ...btnSmall, border: BTN_DESTRUCTIVE_OUTLINE.border, color: BTN_DESTRUCTIVE_OUTLINE.color }}
                        >
                          {t.deleteBtn}
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <ConfirmDialog
        open={confirmState !== null}
        message={confirmState?.message}
        content={confirmState?.content}
        onConfirm={confirmState?.onConfirm}
        onCancel={() => setConfirmState(null)}
        confirmLabel={confirmState?.confirmLabel || t.deleteBtn || 'Delete'}
        cancelLabel={t.formCancel || 'Cancel'}
        confirmColor={confirmState?.confirmColor}
      />
    </div>
  );
}
